// CS-1706 -Phase2 - Progress Note Audit UI Implementation
// Date: 2026-01-21
// Changes: Integrated ProgressNoteAudit code directly into popup to fix focus issues

import React, { useState, useRef } from "react";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import Slide from "@mui/material/Slide";
import { TransitionProps } from "@mui/material/transitions";
import { useAppDispatch } from "../../../redux/slice/redux-hooks";
import { useSelector } from "react-redux";
import {
    setSingleAlertObj,
    SingleAlertInfo,
    fetchDocumentByIdCommonApi,
} from "../../../redux/slice/commonSlice";
import {
    Box,
    Typography,
    useTheme,
    Grid,
    Card,
    CardContent,
    LinearProgress,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Chip,
    IconButton,
} from "@mui/material";
import {
    ExpandMore,
    Remove,
    Download,
    Visibility,
    Close,
    CloudSync,
} from "@mui/icons-material";
import { selectIsTokenAvailable } from "../../../redux/slice/authSlice";
import { RootState } from "../../../redux/store";
import { tokens } from "../../../theme";
import CustomTextField from "../../common/CustomTextField";
import { useForm } from "react-hook-form";
import Button from "../../common/Button";
import { generateAuditReport, generateReportHTML } from "./reportGenerator";
import {
    EditorDrawer,
    VerificationModal,
    EditorOverlay,
} from "./EditorDrawerComponents";
import CancelIcon from "@mui/icons-material/Cancel";
import { useModalStyles } from "../../common/useModalStyles";

interface MedicalNoteAuditPopupProps {
    open: boolean;
    onClose: () => void;
    // CS-1595 - Pre-loaded audit data integration
    initialAuditData?: any;
    auditInProgress?: boolean;
    auditCompleted?: boolean;
    uploadedPdfFile?: File | null;
    // CS-1595 - Additional parameters for audit API
    patientId?: number | string | null;
    docType?: string;
    documentId?: number | string | null;
    // CS-1594 - Callback when audit completes
    onAuditComplete?: (documentId: string, auditData: any) => void;
    dialogSx?: any;
}

interface AuditFormData {
    auditId: string;
    patientName: string;
    fileUrl: string;
    product: string;
    macJurisdiction: string;
    appliedStandards: string;
}

interface ComplianceSection {
    id: string;
    name: string;
    status: string;
    compliance_percent: number;
    requirements?: any[];
    expanded: boolean;
}

const Transition = React.forwardRef(function Transition(
    props: TransitionProps & {
        children: React.ReactElement<any, any>;
    },
    ref: React.Ref<unknown>
) {
    return <Slide direction="down" ref={ref} {...props} />;
});

const MedicalNoteAuditPopup: React.FC<MedicalNoteAuditPopupProps> = ({
    open,
    onClose,
    initialAuditData,
    auditInProgress = false,
    auditCompleted = false,
    uploadedPdfFile = null,
    patientId = null,
    docType = "medical notes",
    documentId = null,
    // CS-1594 - Callback when audit completes
    onAuditComplete,
    dialogSx,
}) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const isDark = theme.palette.mode === "dark";
    // CS-1706 - Hide AI recommendation box when canReAnalyzeAuditNote token is not available
    const canShowMedicalAuditAIRecommendations = useSelector((state: RootState) =>
        selectIsTokenAvailable("canReAnalyzeAuditNote")(state)
);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const dispatch = useAppDispatch();

    // Local state for audit data
    const [currentAudit, setCurrentAudit] = useState<any>(null);
    
    // Local UI state
    const [downloadingReport, setDownloadingReport] = useState(false);
    const [previewMode, setPreviewMode] = useState(false);

    const { cancelBtnSx } = useModalStyles();

    React.useEffect(() => {
        if (initialAuditData) {
            // Handle both data structures:
            // 1. Direct audit data (from CreatePatient/inline button)
            // 2. Wrapped in responseJson (from AiAuditList)
            const auditData = initialAuditData.responseJson || initialAuditData;
            setCurrentAudit(auditData);
        }
    }, [initialAuditData]);

    // Reset audit state when popup closes
    React.useEffect(() => {
        if (!open) {
            setCurrentAudit(null);
            setDownloadingReport(false);
            setPreviewMode(false);
            setEditorOpen(false);
            setEditingRequirement(null);
            setVerificationOpen(false);
            setVerifiedRequirements(new Set());
            setProgressNoteText("");
        }
    }, [open]);

    // Editor drawer state
    const [editorOpen, setEditorOpen] = useState(false);
    const [editingRequirement, setEditingRequirement] = useState<string | null>(null);
    const [verificationOpen, setVerificationOpen] = useState(false);
    const [verifiedRequirements, setVerifiedRequirements] = useState<Set<string>>(new Set());
    const [progressNoteText, setProgressNoteText] = useState<string>("");

    // Load progress note text when audit data is available
    React.useEffect(() => {
        if (currentAudit) {
            // Try multiple possible field names for progress note text
            // Handle nested structure: progress_note.extracted_text
            const noteText = currentAudit.progress_note?.extracted_text ||
                            currentAudit.progress_note || 
                            currentAudit.progressNote?.extracted_text ||
                            currentAudit.progressNote || 
                            currentAudit.note_text || 
                            currentAudit.noteText ||
                            currentAudit.original_text ||
                            currentAudit.text ||
                            "";
            setProgressNoteText(noteText);
        }
    }, [currentAudit]);

    // Get non-compliant requirements
    const getNonCompliantRequirements = () => {
        if (!currentAudit?.categories) return [];
        
        const nonCompliant: any[] = [];
        currentAudit.categories.forEach((category: any) => {
            if (category.requirements && category.requirements.length > 0) {
                category.requirements.forEach((req: any) => {
                    // Case-insensitive status check to handle different API response formats
                    const status = req.status?.toLowerCase();
                    if (status === "fail" || status === "non-compliant") {
                        nonCompliant.push({
                            ...req,
                            categoryId: category.id,
                            categoryName: category.name,
                        });
                    }
                });
            }
        });
        return nonCompliant;
    };

    const nonCompliantRequirements = getNonCompliantRequirements();

    // Extract AI suggestions from audit data
    // AI suggestions should be in currentAudit.ai_suggestions object
    // Each key is a requirement ID, value contains changes array with line-by-line diffs
    // CS-1426 - Wrapped in useCallback to prevent infinite re-renders
    const getAISuggestions = React.useCallback(() => {
        // First check if ai_suggestions object exists at top level (preferred structure)
        if (currentAudit?.ai_suggestions) {
            return currentAudit.ai_suggestions;
        }
        
        // Fallback: Try to construct from requirements if they have changes array
        if (!currentAudit?.categories) {
            return {};
        }
        
        const suggestions: Record<string, any> = {};
        currentAudit.categories.forEach((category: any) => {
            if (category.requirements && category.requirements.length > 0) {
                category.requirements.forEach((req: any) => {
                    // Check if requirement has a changes array (line-by-line diffs)
                    if (req.changes && Array.isArray(req.changes) && req.changes.length > 0) {
                        suggestions[req.id] = {
                            recommendation: req.ai_recommendation || "",
                            changes: req.changes, // Array of {type, lineStart/line_start, oldText/old_text, newText/new_text}
                        };
                    } else if (req.ai_recommendation) {
                        // Fallback: If only ai_recommendation text exists, log warning
                        suggestions[req.id] = {
                            recommendation: req.ai_recommendation,
                            changes: [], // Empty changes array - diff view won't show colors/buttons
                        };
                    }
                });
            }
        });
        
        if (Object.keys(suggestions).length === 0) {
            console.warn("⚠️ No AI suggestions with changes arrays found in audit data");
        }
        
        return suggestions;
    }, [currentAudit]);

    // CS-1426 - Memoize aiSuggestions to prevent infinite re-renders
    const aiSuggestions = React.useMemo(() => getAISuggestions(), [getAISuggestions]);

    // Editor handlers
    const handleFixRequirement = (requirementId: string) => {
        // Validate that the requirement exists before opening drawer
        const requirementExists = nonCompliantRequirements.some(req => req.id === requirementId);
        if (!requirementExists) {
            const alertObj: SingleAlertInfo = {
                message: "Unable to open editor. Requirement not found.",
                alertType: "error",
            };
            dispatch(setSingleAlertObj(alertObj));
            return;
        }
        
        setEditingRequirement(requirementId);
        setEditorOpen(true);
    };

    const handleCancelEdit = () => {
        setEditorOpen(false);
        setEditingRequirement(null);
    };

    // Handler for skipping a requirement and moving to the next one
    const handleSkipRequirement = () => {
        const currentIndex = nonCompliantRequirements.findIndex(req => req.id === editingRequirement);
        
        if (currentIndex < nonCompliantRequirements.length - 1) {
            // Move to next requirement
            const nextRequirement = nonCompliantRequirements[currentIndex + 1];
            setEditingRequirement(nextRequirement.id);
        } else {
            // Last requirement - close drawer
            handleCancelEdit();
        }
    };

    // Handler for editing an AI suggestion
    const handleEditSuggestion = () => {
        // TODO: Implement edit suggestion functionality
        // This could open a text editor or inline editing mode
        
        const alertObj: SingleAlertInfo = {
            message: "Edit suggestion functionality coming soon!",
            alertType: "info",
        };
        dispatch(setSingleAlertObj(alertObj));
    };

    // Handler for accepting AI changes
    const handleAcceptChanges = () => {
        if (!editingRequirement) return;
        
        // Add to verified requirements
        setVerifiedRequirements(prev => {
            const newSet = new Set(prev);
            newSet.add(editingRequirement);
            return newSet;
        });
        
        // Move to next requirement or open verification modal
        const currentIndex = nonCompliantRequirements.findIndex(req => req.id === editingRequirement);
        
        if (currentIndex < nonCompliantRequirements.length - 1) {
            // Move to next requirement
            const nextRequirement = nonCompliantRequirements[currentIndex + 1];
            setEditingRequirement(nextRequirement.id);
        } else {
            // Last requirement - close drawer and open verification modal
            setEditorOpen(false);
            setEditingRequirement(null);
            setVerificationOpen(true);
        }
    };

    const handleVerificationChange = (requirementId: string) => {
        setVerifiedRequirements(prev => {
            const newSet = new Set(prev);
            if (newSet.has(requirementId)) {
                newSet.delete(requirementId);
            } else {
                newSet.add(requirementId);
            }
            return newSet;
        });
    };

    const handleCancelVerification = () => {
        setVerificationOpen(false);
    };

    const handleConfirmVerification = () => {
        // TODO: Implement re-audit logic - send verified requirements to backend
        
        setVerificationOpen(false);
        setVerifiedRequirements(new Set());
        
        // Show success message
        alert(`Re-auditing ${verifiedRequirements.size} requirements...`);
    };

    // Get normalized audit data (handles both API and mock data structures)
    const getNormalizedAuditData = () => {
        if (!currentAudit) return null;

        const metadata = currentAudit.metadata || {};
        const patientName = currentAudit.patient_name || metadata.patient_name;
        const fileUrl = metadata.file_url || currentAudit.filename;
        const productName = metadata.product_name || currentAudit.product_name;
        const macJurisdiction = metadata.mac_jurisdiction || currentAudit.mac_jurisdiction;
        const appliedStandards = metadata.applied_standards || currentAudit.applied_standards || [];

        return {
            ...currentAudit,
            patient_name: patientName,
            file_url: fileUrl,
            product_name: productName,
            mac_jurisdiction: macJurisdiction,
            applied_standards: appliedStandards,
        };
    };

    const normalizedAudit = getNormalizedAuditData();

    // Calculate metrics from audit data
    const getAuditMetrics = () => {
        if (!currentAudit) {
            return { compliance: 0, total_visits: 0, total_wounds: 0, span_days: 0 };
        }

        return {
            compliance: currentAudit.metrics?.compliance_percent || 0,
            total_visits: currentAudit.metrics?.total_visits || 0,
            total_wounds: currentAudit.metrics?.total_wounds || 0,
            span_days: currentAudit.metrics?.span_days || 0,
        };
    };

    const auditMetrics = getAuditMetrics();

    // When the popup is opened from the in-progress icon (no audit data yet),
    // render a graceful empty state instead of empty form fields and 0% bars.
    const showInProgressEmptyState = auditInProgress && !currentAudit;

    const { control, reset , handleSubmit } = useForm<AuditFormData>({
        defaultValues: {
            auditId: "",
            patientName: "",
            fileUrl: "",
            product: "",
            macJurisdiction: "",
            appliedStandards: "",
        },
        // mode: "onChange",
    });

    // Update form when audit data changes
    React.useEffect(() => {
        if (normalizedAudit) {
            reset({
                auditId: normalizedAudit.audit_id || "",
                patientName: normalizedAudit.patient_name || "",
                fileUrl: normalizedAudit.file_url || "",
                product: normalizedAudit.product_name || "",
                macJurisdiction: normalizedAudit.mac_jurisdiction || "",
                appliedStandards: Array.isArray(normalizedAudit.applied_standards)
                    ? normalizedAudit.applied_standards.join(", ")
                    : "",
            });
        }
    }, [normalizedAudit, reset]);

    // Initialize compliance sections from currentAudit or use defaults
    const getInitialSections = (): ComplianceSection[] => {
        if (currentAudit?.categories) {
            return currentAudit.categories.map((cat: any) => ({
                id: cat.id,
                name: cat.name,
                status: cat.status,
                compliance_percent: cat.compliance_percent,
                requirements: cat.requirements || [],
                expanded: false,
            }));
        }

        // Default sections
        return [
            { id: "patient-information", name: "Patient and Visit Information", status: "compliant", compliance_percent: 0, requirements: [], expanded: false },
            { id: "medical-history", name: "Medical History and Prior Treatments", status: "compliant", compliance_percent: 0, requirements: [], expanded: false },
            { id: "wound-characteristics", name: "Wound Characteristics and Measurements", status: "compliant", compliance_percent: 0, requirements: [], expanded: false },
            { id: "wound-condition", name: "Wound Condition", status: "compliant", compliance_percent: 0, requirements: [], expanded: false },
            { id: "medical-necessity", name: "Medical Necessity", status: "compliant", compliance_percent: 0, requirements: [], expanded: false },
            { id: "application-details", name: "Application Details and Follow-Up", status: "compliant", compliance_percent: 0, requirements: [], expanded: false },
            { id: "coding-compliance", name: "Coding and Compliance", status: "compliant", compliance_percent: 0, requirements: [], expanded: false },
        ];
    };

    const [complianceSections, setComplianceSections] = useState<ComplianceSection[]>(getInitialSections());

    // Update sections when currentAudit changes
    React.useEffect(() => {
        if (currentAudit?.categories) {
            setComplianceSections(
                currentAudit.categories.map((cat: any) => ({
                    id: cat.id,
                    name: cat.name,
                    status: cat.status,
                    compliance_percent: cat.compliance_percent,
                    requirements: cat.requirements || [],
                    expanded: false,
                }))
            );
        }
    }, [currentAudit]);

    const toggleCategory = (categoryId: string) => {
        setComplianceSections((prev) =>
            prev.map((section) =>
                section.id === categoryId
                    ? { ...section, expanded: !section.expanded }
                    : section
            )
        );
    };

    const getComplianceColor = (percentage: number) => {
        if (percentage >= 80) return colors.success[400];
        if (percentage >= 60) return colors.warning[400];
        return colors.error[400];
    };

    // CS-1706 - Keep severity chip labels readable in light mode with high-contrast color pairs.
    const getSeverityChipStyles = (severity?: string) => {
        const normalizedSeverity = String(severity || "").toUpperCase();

        if (normalizedSeverity === "CRITICAL") {
            return {
                backgroundColor: isDark ? colors.error[500] : "#FEE2E2",
                color: isDark ? "#FFFFFF" : "#991B1B",
            };
        }

        if (normalizedSeverity === "MAJOR") {
            return {
                backgroundColor: isDark ? colors.warning[500] : "#FEF3C7",
                color: isDark ? "#FFFFFF" : "#92400E",
            };
        }

        return {
            backgroundColor: isDark ? colors.info[500] : "#DBEAFE",
            color: isDark ? "#FFFFFF" : "#1E40AF",
        };
    };

    const handleFileUploadClick = () => {
        fileInputRef.current?.click();
    };

    const handleDownloadReport = async () => {
        if (!currentAudit) return;
        setDownloadingReport(true);
        try {
            const auditDataForReport = {
                ...currentAudit,
                patient_name: normalizedAudit?.patient_name || currentAudit.patient_name || "Patient",
                product_name: normalizedAudit?.product_name || currentAudit.product_name,
                file_url: normalizedAudit?.file_url || currentAudit.filename,
                mac_jurisdiction: normalizedAudit?.mac_jurisdiction || currentAudit.mac_jurisdiction,
                applied_standards: normalizedAudit?.applied_standards || currentAudit.applied_standards,
            };

            const patientName = auditDataForReport.patient_name || "Patient";
            await generateAuditReport(
                auditDataForReport,
                patientName,
                canShowMedicalAuditAIRecommendations
            );
        } catch (error) {
            alert("Failed to generate PDF report. Please try again.");
        } finally {
            setDownloadingReport(false);
        }
    };

    const handleDownloadDocument = async () => {
        // Get documentId from audit data
        // Check both: root level (from API response) and inside currentAudit
        const docId = initialAuditData?.documentId || currentAudit?.documentId;
        
        if (!docId) {
            const alertObj: SingleAlertInfo = {
                message: "No document available to download.",
                alertType: "warning",
            };
            dispatch(setSingleAlertObj(alertObj));
            return;
        }

        try {
            // Fetch document from server using documentId
            const actionResult = await dispatch(fetchDocumentByIdCommonApi(Number(docId)));
            
            if (actionResult?.payload) {
                const documentData = actionResult.payload;
                
                if (documentData.content) {
                    // Convert base64 content to blob
                    const byteCharacters = atob(documentData.content);
                    const byteNumbers = new Array(byteCharacters.length);
                    for (let i = 0; i < byteCharacters.length; i++) {
                        byteNumbers[i] = byteCharacters.charCodeAt(i);
                    }
                    const byteArray = new Uint8Array(byteNumbers);
                    const blob = new Blob([byteArray], { type: documentData.fileType });
                    
                    // Create download link
                    const url = window.URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = documentData.documentName || 'document.pdf';
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    
                    // Clean up the blob URL
                    window.URL.revokeObjectURL(url);

                    const alertObj: SingleAlertInfo = {
                        message: "Document download started.",
                        alertType: "success",
                    };
                    dispatch(setSingleAlertObj(alertObj));
                } else {
                    throw new Error("Document content not found");
                }
            } else {
                throw new Error("Failed to fetch document");
            }
        } catch (error) {
            const alertObj: SingleAlertInfo = {
                message: "Failed to download document. Please try again.",
                alertType: "error",
            };
            dispatch(setSingleAlertObj(alertObj));
        }
    };

    const handlePreview = () => {
        setPreviewMode(true);
    };

    const handleClosePreview = () => {
        setPreviewMode(false);
    };

    const handlePrintToPDF = () => {
        const reportBody = document.getElementById("report-body");
        if (reportBody) {
            const height = reportBody.scrollHeight;
            const heightInInches = height / 96;

            const styleId = "dynamic-print-size";
            let styleElement = document.getElementById(styleId);
            if (!styleElement) {
                styleElement = document.createElement("style");
                styleElement.id = styleId;
                document.head.appendChild(styleElement);
            }
            styleElement.innerHTML = `
                @media print {
                    @page { size: 8.5in ${heightInInches}in; margin: 0; }
                }
            `;
        }
        window.print();
    };

    return (
        <Dialog
            open={open}
            TransitionComponent={Transition}
            keepMounted
            onClose={onClose}
            aria-labelledby="medical-note-audit-dialog"
            maxWidth="xl"
            fullWidth
            disableEnforceFocus
            sx={{
                ...(dialogSx || {}),
                "& .MuiPaper-root": {
                    borderRadius: "12px",
                    p: 0,
                    border: `2px solid ${theme.palette.mode === "dark" ? "#464954ff" : "#e2e8f0"}`,
                    boxShadow: theme.palette.mode === "dark" 
                        ? "0 0 20px rgba(0, 0, 0, 0.6)" 
                        : "0 0 20px rgba(0, 0, 0, 0.15)",
                },
            }}
        >
            <DialogContent
                sx={{
                    p: { xs: 1.5, sm: 2, md: 3 },
                    background: theme.palette.mode === "dark"
                        ? "linear-gradient(270deg, #131319ff, #1d2931)"
                        : "#ffffff",
                    position: "relative",
                    overflow: "hidden",
                }}
            >
                <form onSubmit={handleSubmit((data => console.log(data)))}>
                <Box
                    sx={{
                        minHeight: { xs: "60vh", md: "70vh" },
                        maxHeight: { xs: "75vh", md: "80vh" },
                        overflow: "auto",
                        p: { xs: 1, sm: 1.5, md: 2 },
                    }}
                >
                    {/* Header with Action Buttons */}
                    <Box sx={{ 
                        display: "flex", 
                        flexDirection: { xs: "column", md: "row" },
                        justifyContent: "space-between", 
                        alignItems: { xs: "flex-start", md: "center" }, 
                        mb: 2,
                        gap: { xs: 2, md: 0 }
                    }}>
                        <Box>
                            <Typography
                                variant="h4"
                                fontWeight="bold"
                                color={colors.grey[100]}
                                sx={{ mb: 0.5, fontSize: { xs: "1.25rem", sm: "1.5rem", md: "2rem" } }}
                            >
                                Medical Note Audit
                            </Typography>
                            <Typography variant="body2" color={colors.grey[300]} sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}>
                                Audit progress notes and make edits for compliance.
                            </Typography>
                        </Box>
                        
                        {/* Action Buttons */}
                        <Box sx={{ 
                            display: "flex", 
                            gap: { xs: 1, sm: 1.5, md: 2 },
                            flexWrap: "wrap",
                            width: { xs: "100%", md: "auto" }
                        }}>
                            {/* <input
                                ref={fileInputRef}
                                type="file"
                                accept=".pdf"
                                onChange={handleFileChange}
                                style={{ display: "none" }}
                            /> */}
                            {/* CS-1595 - Hide Upload PDF button when audit data exists OR when PDF is already uploaded */}
                            {/* {!initialAuditData && !currentAudit && !uploadedPdfFile && (
                                <Button
                                    variant="outlined"
                                    startIcon={<CloudUpload />}
                                    onClick={handleFileUploadClick}
                                    disabled={uploading || auditLoading}
                                    sx={{
                                        borderColor: colors.grey[400],
                                        color: colors.grey[100],
                                        fontSize: { xs: "0.75rem", sm: "0.875rem" },
                                        px: { xs: 1.5, sm: 2 },
                                        "&:hover": {
                                            borderColor: colors.grey[300],
                                            backgroundColor: "rgba(255, 255, 255, 0.05)",
                                        },
                                    }}
                                >
                                    {uploading ? "Analyzing..." : auditLoading ? "Loading..." : "Upload PDF"}
                                </Button>
                            )} */}
                            <Button
                                variant="outlined"
                                startIcon={<Download />}
                                onClick={handleDownloadReport}
                                disabled={!currentAudit || downloadingReport}
                                sx={{
                                    borderColor: currentAudit ? colors.primary[400] : colors.grey[600],
                                    color: currentAudit ? colors.primary[400] : colors.grey[500],
                                    fontSize: { xs: "0.75rem", sm: "0.875rem" },
                                    px: { xs: 1.5, sm: 2 },
                                    "&:hover": currentAudit ? {
                                        borderColor: colors.primary[300],
                                        backgroundColor: "rgba(0, 184, 217, 0.05)",
                                    } : {},
                                }}
                            >
                                {downloadingReport ? "Generating..." : "Download Report"}
                            </Button>
                            {/* CS-1595 - Hide Preview button when no audit data exists */}
                            {currentAudit && (
                                <Button
                                    color="success"
                                    startIcon={<Visibility />}
                                    onClick={handlePreview}
                                >
                                    Preview
                                </Button>
                            )}
                                <Button
                                    variant="outlined"
                                    startIcon={<CancelIcon sx={{ fontSize: "1rem !important" }} />}
                                    onClick={onClose}
                                    sx={cancelBtnSx}
                                >
                                    Close
                                </Button>
                        </Box>
                    </Box>


                   {/* CS-1773 - Run Medical Note Audit AI Tool in Background */}
                   {showInProgressEmptyState ? (
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                justifyContent: "center",
                                textAlign: "center",
                                py: { xs: 6, md: 10 },
                                px: { xs: 2, md: 4 },
                                gap: 2,
                            }}
                        >
                            <Box
                                sx={{
                                    width: 96,
                                    height: 96,
                                    borderRadius: "50%",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    // Theme green (project primary palette) — matches the
                                    // inline button's in-progress icon for visual continuity.
                                    background: isDark
                                        ? "linear-gradient(135deg, rgba(0, 167, 111, 0.22), rgba(0, 167, 111, 0.14))"
                                        : "linear-gradient(135deg, rgba(0, 167, 111, 0.14), rgba(0, 167, 111, 0.08))",
                                    border: `2px solid ${colors.primary[300]}`,
                                }}
                            >
                                <CloudSync
                                    sx={{
                                        fontSize: 56,
                                        color: colors.primary[300],
                                    }}
                                />
                            </Box>
                            <Typography
                                variant="h5"
                                fontWeight={700}
                                color={colors.grey[100]}
                                sx={{ mt: 1 }}
                            >
                                Medical Note Audit is in progress
                            </Typography>
                            <Typography
                                variant="body1"
                                color={colors.grey[300]}
                                sx={{ maxWidth: 520 }}
                            >
                                We're processing your medical note. You can keep working —
                                we'll notify you when the audit report is ready. The
                                "Medical Note Report" button will appear automatically once
                                the audit is complete.
                            </Typography>
                        </Box>
                    ) : (
                    <>
                    {/* Audit Information Cards */}
                    <Grid container spacing={{ xs: 1.5, sm: 2 }} sx={{ mb: 2 }}>
                        {/* Left Column - Audit Details */}
                        <Grid item xs={12} md={6}>
                            <Card
                                sx={{
                                    backgroundColor: isDark ? colors.grey[900] : colors.grey[900],
                                    border: `1px solid ${isDark ? colors.grey[700] : colors.grey[300]}`,
                                    height: "100%",
                                }}
                            >
                                <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                                    <Grid container spacing={{ xs: 1, sm: 1.5 }}>
                                        <Grid item xs={12}>
                                            <Typography
                                                variant="caption"
                                                color={colors.grey[400]}
                                                sx={{ mb: 0.5, display: "block", textTransform: "uppercase" }}
                                            >
                                                Audit ID
                                            </Typography>
                                            <CustomTextField
                                                control={control}
                                                controllerName="auditId"
                                                placeholder="Audit ID"
                                                size="small"
                                                readOnly
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Typography
                                                variant="caption"
                                                color={colors.grey[400]}
                                                sx={{ mb: 0.5, display: "block", textTransform: "uppercase" }}
                                            >
                                                Patient
                                            </Typography>
                                            <CustomTextField
                                                control={control}
                                                controllerName="patientName"
                                                placeholder="Patient Name"
                                                size="small"
                                                readOnly
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Typography
                                                variant="caption"
                                                color={colors.grey[400]}
                                                sx={{ mb: 0.5, display: "block", textTransform: "uppercase" }}
                                            >
                                               Document
                                            </Typography>
                                            <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                                                <Box sx={{ flex: 1 }}>
                                                    <CustomTextField
                                                        control={control}
                                                        controllerName="fileUrl"
                                                        placeholder="Filename"
                                                        size="small"
                                                        readOnly
                                                    />
                                                </Box>
                                                <IconButton
                                                    onClick={handleDownloadDocument}
                                                    disabled={!(initialAuditData?.documentId || currentAudit?.documentId)}
                                                    sx={{
                                                        color: (initialAuditData?.documentId || currentAudit?.documentId) 
                                                            ? isDark ? colors.primary[200] : colors.primary[500]
                                                            : colors.grey[600],
                                                        backgroundColor: isDark ? colors.grey[800] : colors.grey[200],
                                                        "&:hover": {
                                                            backgroundColor: (initialAuditData?.documentId || currentAudit?.documentId)
                                                                ? isDark ? colors.grey[700] : colors.grey[300]
                                                                : undefined,
                                                        },
                                                        "&:disabled": {
                                                            color: colors.grey[600],
                                                        },
                                                    }}
                                                    title="Download Document"
                                                >
                                                    <Download />
                                                </IconButton>
                                            </Box>
                                        </Grid>
                                    </Grid>
                                </CardContent>
                            </Card>
                        </Grid>

                        {/* Right Column - Product Details */}
                        <Grid item xs={12} md={6}>
                            <Card
                                sx={{
                                    backgroundColor:isDark ? colors.grey[900] : colors.grey[900],
                                    border: `1px solid ${isDark ? colors.grey[700] : colors.grey[300]}`,
                                    height: "100%",
                                }}
                            >
                                <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                                    <Grid container spacing={{ xs: 1, sm: 1.5 }}>
                                        <Grid item xs={12}>
                                            <Typography
                                                variant="caption"
                                                color={colors.grey[400]}
                                                sx={{ mb: 0.5, display: "block", textTransform: "uppercase" }}
                                            >
                                                Product
                                            </Typography>
                                            <CustomTextField
                                                control={control}
                                                controllerName="product"
                                                placeholder="Not specified"
                                                size="small"
                                                readOnly
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Typography
                                                variant="caption"
                                                color={colors.grey[400]}
                                                sx={{ mb: 0.5, display: "block", textTransform: "uppercase" }}
                                            >
                                                MAC Jurisdiction
                                            </Typography>
                                            <CustomTextField
                                                control={control}
                                                controllerName="macJurisdiction"
                                                placeholder="Not specified"
                                                size="small"
                                                readOnly
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Typography
                                                variant="caption"
                                                color={colors.grey[400]}
                                                sx={{ mb: 0.5, display: "block", textTransform: "uppercase" }}
                                            >
                                                Applied Standards
                                            </Typography>
                                            {normalizedAudit?.applied_standards && normalizedAudit.applied_standards.length > 0 ? (
                                                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 0.5 }}>
                                                    {normalizedAudit.applied_standards.map((standard: string, idx: number) => (
                                                        <Chip
                                                            key={idx}
                                                            label={standard}
                                                            size="small"
                                                            sx={{
                                                                backgroundColor: "rgba(0, 184, 217, 0.2)",
                                                                color: colors.primary[400],
                                                                fontWeight: "500",
                                                            }}
                                                        />
                                                    ))}
                                                </Box>
                                            ) : (
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        color: colors.grey[500],
                                                        fontStyle: "italic",
                                                        p: 1,
                                                        border: `1px dashed ${colors.grey[600]}`,
                                                        borderRadius: 1,
                                                    }}
                                                >
                                                    None specified
                                                </Typography>
                                            )}
                                        </Grid>
                                    </Grid>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>

                    {/* Summary Cards */}
                    <Grid container spacing={{ xs: 1.5, sm: 2 }} sx={{ mb: 2 }}>
                        <Grid item xs={6} sm={6} md={3}>
                            <Card
                                sx={{
                                    backgroundColor: isDark ? colors.grey[900] : colors.grey[900],
                                    border: `1px solid ${isDark ? colors.grey[700] : colors.grey[300]}`,
                                }}
                            >
                                <CardContent sx={{ p: { xs: 1, sm: 1.5 } }}>
                                    <Typography variant="caption" color={colors.grey[400]} sx={{ textTransform: "uppercase", fontSize: { xs: "0.65rem", sm: "0.75rem" } }}>
                                        COMPLIANCE
                                    </Typography>
                                    <Typography variant="h3" fontWeight="bold" color={colors.grey[100]} sx={{ fontSize: { xs: "1.5rem", sm: "2rem", md: "2.5rem" } }}>
                                        {auditMetrics.compliance}%
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={6} sm={6} md={3}>
                            <Card
                                sx={{
                                    backgroundColor: isDark ? colors.grey[900] : colors.grey[900],
                                    border: `1px solid ${isDark ? colors.grey[700] : colors.grey[300]}`,
                                }}
                            >
                                <CardContent sx={{ p: { xs: 1, sm: 1.5 } }}>
                                    <Typography variant="caption" color={colors.grey[400]} sx={{ textTransform: "uppercase", fontSize: { xs: "0.65rem", sm: "0.75rem" } }}>
                                        VISITS
                                    </Typography>
                                    <Typography variant="h3" fontWeight="bold" color={colors.grey[100]} sx={{ fontSize: { xs: "1.5rem", sm: "2rem", md: "2.5rem" } }}>
                                        {String(auditMetrics.total_visits).padStart(2, "0")}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={6} sm={6} md={3}>
                            <Card
                                sx={{
                                    backgroundColor:isDark ? colors.grey[900] : colors.grey[900],
                                    border: `1px solid ${isDark ? colors.grey[700] : colors.grey[300]}`,
                                }}
                            >
                                <CardContent sx={{ p: { xs: 1, sm: 1.5 } }}>
                                    <Typography variant="caption" color={colors.grey[400]} sx={{ textTransform: "uppercase", fontSize: { xs: "0.65rem", sm: "0.75rem" } }}>
                                        WOUNDS
                                    </Typography>
                                    <Typography variant="h3" fontWeight="bold" color={colors.grey[100]} sx={{ fontSize: { xs: "1.5rem", sm: "2rem", md: "2.5rem" } }}>
                                        {String(auditMetrics.total_wounds).padStart(2, "0")}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={6} sm={6} md={3}>
                            <Card
                                sx={{
                                    backgroundColor: isDark ? colors.grey[900] : colors.grey[900],
                                    border: `1px solid ${isDark ? colors.grey[700] : colors.grey[300]}`,
                                }}
                            >
                                <CardContent sx={{ p: { xs: 1, sm: 1.5 } }}>
                                    <Typography variant="caption" color={colors.grey[400]} sx={{ textTransform: "uppercase", fontSize: { xs: "0.65rem", sm: "0.75rem" } }}>
                                        SPAN DAYS
                                    </Typography>
                                    <Typography variant="h3" fontWeight="bold" color={colors.grey[100]} sx={{ fontSize: { xs: "1.5rem", sm: "2rem", md: "2.5rem" } }}>
                                        {auditMetrics.span_days}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>

                    {/* Compliance Sections */}
                    <Box>
                        {complianceSections.map((section) => (
                            <Accordion
                                key={section.id}
                                expanded={section.expanded}
                                onChange={() => toggleCategory(section.id)}
                                sx={{
                                    mb: 1,
                                    backgroundColor: isDark ? colors.grey[900] : colors.grey[900],
                                    border: `1px solid ${isDark ? colors.grey[700] : colors.grey[300]}`,
                                    "&:before": {
                                        display: "none",
                                    },
                                    borderRadius: "8px !important",
                                }}
                            >
                                <AccordionSummary
                                    expandIcon={
                                        <IconButton size="small">
                                            {section.expanded ? (
                                                <Remove sx={{ color: colors.grey[300] }} />
                                            ) : (
                                                <ExpandMore sx={{ color: colors.grey[300] }} />
                                            )}
                                        </IconButton>
                                    }
                                    sx={{
                                        "& .MuiAccordionSummary-content": {
                                            display: "flex",
                                            flexDirection: { xs: "column", md: "row" },
                                            justifyContent: "space-between",
                                            alignItems: { xs: "flex-start", md: "center" },
                                            my: 0.5,
                                            gap: { xs: 1, md: 0 },
                                        },
                                    }}
                                >
                                    <Box sx={{ flex: 1, width: { xs: "100%", md: "auto" } }}>
                                        <Typography
                                            variant="body1"
                                            fontWeight="600"
                                            color={colors.grey[100]}
                                            sx={{ 
                                                textTransform: "uppercase", 
                                                fontSize: { xs: "0.75rem", sm: "0.875rem" },
                                                lineHeight: 1.3,
                                            }}
                                        >
                                            {section.name}
                                        </Typography>
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                color: currentAudit
                                                    ? section.status === "compliant" || section.status === "Compliant"
                                                        ? colors.success[400]
                                                        : colors.error[400]
                                                    : colors.grey[400],
                                                fontWeight: "600",
                                                fontSize: { xs: "0.7rem", sm: "0.75rem" },
                                            }}
                                        >
                                            {currentAudit
                                                ? section.status === "compliant" || section.status === "Compliant"
                                                    ? "Compliant"
                                                    : "Non-Compliant"
                                                : "Compliance"}
                                        </Typography>
                                    </Box>
                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: { xs: 1, sm: 1.5, md: 2 },
                                            mr: { xs: 0, md: 2 },
                                            width: { xs: "100%", md: "auto" },
                                        }}
                                    >
                                        <Box sx={{ 
                                            width: { xs: "100%", sm: 150, md: 200 },
                                            flex: { xs: 1, md: "none" },
                                        }}>
                                            <LinearProgress
                                                variant="determinate"
                                                value={section.compliance_percent}
                                                sx={{
                                                    height: { xs: 5, sm: 6 },
                                                    borderRadius: 3,
                                                    backgroundColor: isDark ? "#4a5568" : "#e2e8f0",
                                                    "& .MuiLinearProgress-bar": {
                                                        backgroundColor: getComplianceColor(section.compliance_percent),
                                                        borderRadius: 3,
                                                    },
                                                }}
                                            />
                                        </Box>
                                        <Chip
                                            label={currentAudit && (section.status === "compliant" || section.status === "Compliant") ? "PASS" : currentAudit ? "FAIL" : ""}
                                            size="small"
                                            sx={{
                                                backgroundColor: currentAudit
                                                    ? section.status === "compliant" || section.status === "Compliant"
                                                        ? colors.success[400]
                                                        : colors.error[400]
                                                    : colors.grey[600],
                                                color: "white",
                                                fontWeight: "600",
                                                minWidth: { xs: 45, sm: 50 },
                                                height: { xs: 22, sm: 24 },
                                                fontSize: { xs: "0.65rem", sm: "0.75rem" },
                                                flexShrink: 0,
                                            }}
                                        />
                                    </Box>
                                </AccordionSummary>
                                <AccordionDetails sx={{ pt: 0, pb: 1.5, px: { xs: 1, sm: 1.5, md: 2 } }}>
                                    {section.requirements && section.requirements.length > 0 ? (
                                        <Box sx={{ p: { xs: 1, sm: 1.5, md: 2 } }}>
                                            {section.requirements.map((requirement: any) => (
                                                <Box
                                                    key={requirement.id}
                                                    sx={{
                                                        mb: 2,
                                                        p: { xs: 1.5, sm: 2 },
                                                        borderRadius: 1,
                                                        backgroundColor: isDark ? "#1A1F2E" : "#f9fafb",
                                                        border: `1px solid ${isDark ? "#4a5568" : "#e2e8f0"}`,
                                                        borderLeft: `4px solid ${
                                                            requirement.severity === "CRITICAL"
                                                                ? colors.error[500]
                                                                : requirement.severity === "MAJOR"
                                                                ? colors.warning[500]
                                                                : colors.info[500]
                                                        }`,
                                                    }}
                                                >
                                                    <Box sx={{ 
                                                        display: "flex", 
                                                        gap: { xs: 0.5, sm: 1 }, 
                                                        mb: 1,
                                                        flexWrap: "wrap",
                                                    }}>
                                                        <Chip
                                                            label={(requirement.id || "REQ").replace(/_/g, "-")}
                                                            size="small"
                                                            sx={{
                                                                backgroundColor: isDark ? "#2A2D38" : "#e2e8f0",
                                                                color: colors.primary[400],
                                                                fontFamily: "monospace",
                                                                fontWeight: "bold",
                                                                fontSize: { xs: "0.65rem", sm: "0.75rem" },
                                                                height: { xs: 20, sm: 24 },
                                                            }}
                                                        />
                                                        <Chip
                                                            label={requirement.severity}
                                                            size="small"
                                                            sx={{
                                                                ...getSeverityChipStyles(requirement.severity),
                                                                fontWeight: "bold",
                                                                fontSize: { xs: "0.65rem", sm: "0.75rem" },
                                                                height: { xs: 20, sm: 24 },
                                                            }}
                                                        />
                                                    </Box>
                                                    <Typography 
                                                        variant="body2" 
                                                        fontWeight="bold" 
                                                        sx={{ 
                                                            mb: 1,
                                                            fontSize: { xs: "0.8rem", sm: "0.875rem" },
                                                        }}
                                                    >
                                                        {requirement.name}
                                                    </Typography>
                                                    {requirement.evidence && (
                                                        <Box sx={{ mb: 1 }}>
                                                            <Typography
                                                                variant="caption"
                                                                sx={{
                                                                    color: colors.primary[400],
                                                                    textTransform: "uppercase",
                                                                    fontWeight: "600",
                                                                    fontSize: { xs: "0.65rem", sm: "0.75rem" },
                                                                }}
                                                            >
                                                                Evidence Found
                                                            </Typography>
                                                            <Typography
                                                                variant="body2"
                                                                sx={{
                                                                    mt: 0.5,
                                                                    p: { xs: 0.75, sm: 1 },
                                                                    backgroundColor: isDark ? "#151A24" : "#f3f4f6",
                                                                    borderLeft: `3px solid ${colors.grey[600]}`,
                                                                    color: colors.grey[400],
                                                                    fontStyle: "italic",
                                                                    fontSize: { xs: "0.75rem", sm: "0.875rem" },
                                                                }}
                                                            >
                                                                {requirement.evidence.context || "No evidence found"}
                                                            </Typography>
                                                        </Box>
                                                    )}
                                                    {requirement.missing_elements && requirement.missing_elements.length > 0 && (
                                                        <Box sx={{ mb: 1 }}>
                                                            <Typography
                                                                variant="caption"
                                                                sx={{
                                                                    color: colors.error[400],
                                                                    textTransform: "uppercase",
                                                                    fontWeight: "600",
                                                                    fontSize: { xs: "0.65rem", sm: "0.75rem" },
                                                                }}
                                                            >
                                                                Missing Elements ({requirement.missing_elements.length})
                                                            </Typography>
                                                            <Box component="ul" sx={{ mt: 0.5, pl: { xs: 1.5, sm: 2 }, mb: 0 }}>
                                                                {requirement.missing_elements.map((element: string, idx: number) => (
                                                                    <Typography
                                                                        key={idx}
                                                                        component="li"
                                                                        variant="body2"
                                                                        sx={{ 
                                                                            color: colors.grey[300],
                                                                            fontSize: { xs: "0.75rem", sm: "0.875rem" },
                                                                        }}
                                                                    >
                                                                        {element}
                                                                    </Typography>
                                                                ))}
                                                            </Box>
                                                        </Box>
                                                    )}
                                                    {canShowMedicalAuditAIRecommendations && requirement.ai_recommendation && (
                                                        <Box
                                                            sx={{
                                                                mt: 1,
                                                                p: { xs: 1, sm: 1.5 },
                                                                borderRadius: 1,
                                                                backgroundColor: isDark ? "#151A24" : "#f0f9ff",
                                                                border: `2px solid ${colors.primary[400]}`,
                                                            }}
                                                        >
                                                            <Typography
                                                                variant="caption"
                                                                sx={{
                                                                    color: colors.primary[400],
                                                                    textTransform: "uppercase",
                                                                    fontWeight: "600",
                                                                    fontSize: { xs: "0.65rem", sm: "0.75rem" },
                                                                }}
                                                            >
                                                                ✨ AI Recommendation
                                                            </Typography>
                                                            <Typography 
                                                                variant="body2" 
                                                                sx={{ 
                                                                    mt: 0.5, 
                                                                    mb: 2,
                                                                    color: colors.grey[200],
                                                                    fontSize: { xs: "0.75rem", sm: "0.875rem" },
                                                                }}
                                                            >
                                                                {requirement.ai_recommendation}
                                                            </Typography>
                                                            <Button
                                                                variant="contained"
                                                                onClick={() => handleFixRequirement(requirement.id)}
                                                                sx={{
                                                                    backgroundColor: colors.primary[400],
                                                                    color: "#FFFFFF",
                                                                    fontSize: { xs: "0.75rem", sm: "0.875rem" },
                                                                    px: 2,
                                                                    py: 1,
                                                                    "&:hover": {
                                                                        backgroundColor: colors.primary[300],
                                                                        transform: "scale(1.02)",
                                                                        boxShadow: "0 0 12px rgba(0, 184, 217, 0.4)",
                                                                    },
                                                                    transition: "all 0.2s",
                                                                }}
                                                            >
                                                                Fix with Editing Wizard →
                                                            </Button>
                                                        </Box>
                                                    )}
                                                </Box>
                                            ))}
                                        </Box>
                                    ) : (
                                        <Typography 
                                            variant="body2" 
                                            color={colors.grey[300]} 
                                            sx={{ 
                                                p: { xs: 1.5, sm: 2 },
                                                fontSize: { xs: "0.75rem", sm: "0.875rem" },
                                            }}
                                        >
                                            Detailed compliance information will be displayed here.
                                        </Typography>
                                    )}
                                </AccordionDetails>
                            </Accordion>
                        ))}
                    </Box>
                    </>
                    )}
                </Box>
                </form>
            </DialogContent>

            {/* Preview Modal */}
            {previewMode && (
                <Box
                    sx={{
                        position: "fixed",
                        inset: 0,
                        zIndex: 9999,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: "rgba(0, 0, 0, 0.5)",
                        p: { xs: 1, sm: 2 },
                    }}
                    id="report-print-modal"
                    onClick={handleClosePreview}
                >
                    <Box
                        sx={{
                            backgroundColor: "#FFFFFF",
                            borderRadius: { xs: "4px", sm: "8px" },
                            boxShadow: "0 10px 40px rgba(0, 0, 0, 0.3)",
                            maxWidth: "1200px",
                            width: "100%",
                            maxHeight: { xs: "95vh", sm: "90vh" },
                            overflow: "hidden",
                            display: "flex",
                            flexDirection: "column",
                        }}
                        id="report-content"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <Box
                            sx={{
                                backgroundColor: "#FFFFFF",
                                borderBottom: "1px solid #e5e7eb",
                                p: { xs: 1.5, sm: 2 },
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                flexShrink: 0,
                            }}
                            id="report-header"
                        >
                            <Typography 
                                variant="h5" 
                                fontWeight="bold" 
                                sx={{ 
                                    color: "#121212",
                                    fontSize: { xs: "1rem", sm: "1.25rem", md: "1.5rem" }
                                }}
                            >
                                Report Preview
                            </Typography>
                            <Box sx={{ display: "flex", gap: { xs: 0.5, sm: 1 } }}>
                                <Button
                                    variant="contained"
                                    onClick={handlePrintToPDF}
                                    sx={{
                                        backgroundColor: "#3b82f6",
                                        color: "#FFFFFF",
                                        fontSize: { xs: "0.75rem", sm: "0.875rem" },
                                        px: { xs: 1.5, sm: 2 },
                                        py: { xs: 0.5, sm: 1 },
                                        minWidth: "auto",
                                        "&:hover": {
                                            backgroundColor: "#2563eb",
                                        },
                                    }}
                                >
                                    Print to PDF
                                </Button>
                                <IconButton
                                    onClick={handleClosePreview}
                                    size="small"
                                    sx={{
                                        color: "#6b7280",
                                        "&:hover": {
                                            color: "#374151",
                                        },
                                    }}
                                >
                                    <Close fontSize="small" />
                                </IconButton>
                            </Box>
                        </Box>
                        <Box
                            sx={{
                                p: { xs: 2, sm: 3, md: 4 },
                                color: "#121212",
                                overflow: "auto",
                                flex: 1,
                                "& *": {
                                    color: "#121212 !important",
                                },
                                "& h1": {
                                    fontSize: { xs: "1.5rem", sm: "2rem", md: "2.25rem" },
                                },
                                "& h2": {
                                    fontSize: { xs: "1.125rem", sm: "1.25rem", md: "1.5rem" },
                                },
                                "& p, & div": {
                                    fontSize: { xs: "0.75rem", sm: "0.875rem", md: "1rem" },
                                },
                            }}
                            id="report-body"
                            dangerouslySetInnerHTML={{
                                __html: generateReportHTML(
                                    currentAudit,
                                    currentAudit?.patient_name || normalizedAudit?.patient_name ,
                                    currentAudit?.filename || normalizedAudit?.file_url,
                                    canShowMedicalAuditAIRecommendations
                                ),
                            }}
                        />
                    </Box>
                </Box>
            )}

            {/* Editor Overlay */}
            <EditorOverlay editorOpen={editorOpen} handleCancelEdit={handleCancelEdit} />

            {/* Editor Drawer */}
            <EditorDrawer
                editorOpen={editorOpen}
                editingRequirement={editingRequirement}
                nonCompliantRequirements={nonCompliantRequirements}
                colors={colors}
                handleCancelEdit={handleCancelEdit}
                progressNoteText={progressNoteText}
                aiSuggestions={aiSuggestions}
                onSkipRequirement={handleSkipRequirement}
                onEditSuggestion={handleEditSuggestion}
                onAcceptChanges={handleAcceptChanges}
            />

            {/* Verification Modal */}
            <VerificationModal
                verificationOpen={verificationOpen}
                verifiedRequirements={verifiedRequirements}
                nonCompliantRequirements={nonCompliantRequirements}
                colors={colors}
                handleVerificationChange={handleVerificationChange}
                handleCancelVerification={handleCancelVerification}
                handleConfirmVerification={handleConfirmVerification}
            />
        </Dialog>
    );
};

export default MedicalNoteAuditPopup;
